import { DateTime } from 'luxon';
import {
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
  useRef,
  useState,
} from 'react';
import { Control, FieldValues, Path, useController } from 'react-hook-form';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import { Input } from '@/components/Forms';
import Text from '@/components/Text';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { buildCalendar } from '@/utils/buildCalendar';
import { scrollBarStyles } from '@/utils/theme';

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const pad = (time: string) => {
  if (time.length === 1) {
    return '0' + time;
  }

  return time;
};

interface BoxProps {
  $openUpward?: boolean;
}

export const Box = styled.div<BoxProps>`
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  box-shadow:
    0px 8px 22px -6px rgba(33, 29, 30, 0.08),
    0px 14px 64px -4px rgba(33, 29, 30, 0.08);
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.white};
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  right: 0;
  ${({ $openUpward }) =>
    $openUpward
      ? css`
          bottom: calc(100% + 8px);
          top: auto;
        `
      : css`
          top: calc(100% + 8px);
        `}
  width: 100%;
  max-width: 320px;
  z-index: 1;
`;

export const Break = styled.div`
  background-color: ${({ theme }) => theme.colors.lightGray};
  height: 1px;
  width: 100%;
`;

interface BoxTopProps {
  $withoutTime: boolean;
}

const BoxTop = styled.div<BoxTopProps>`
  padding: 32px 30px ${({ $withoutTime }) => ($withoutTime ? '32px' : '16px')};
  width: 320px;
`;

const BoxBottom = styled.div`
  padding: 0 32px 34px;
  width: 320px;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border-radius: 4px;
  grid-gap: 2px 5px;
`;

export const Days = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-gap: 5px;
  margin-bottom: 6px;
`;

interface DayProps {
  $isOtherMonthDay?: boolean;
  $isSelected?: boolean;
  $isDayinBetween?: boolean;
}

export const Day = styled(Text)<DayProps>`
  height: 32px;
  justify-content: center;
  display: flex;
  align-items: center;
  cursor: pointer;
  border-radius: 50%;
  width: 100%;
  ${({ $isOtherMonthDay }) =>
    $isOtherMonthDay &&
    css`
      opacity: 0.4;
    `}
  ${({ $isSelected }) =>
    $isSelected &&
    css`
      opacity: 1;
      background: ${({ theme }) => theme.colors.error};
      color: ${({ theme }) => theme.colors.white};
    `};
  ${({ $isDayinBetween }) =>
    $isDayinBetween &&
    css`
      background: ${({ theme }) => theme.colors.grayscaleLight};
      border-radius: 0;
    `};
`;

export const WeekDay = styled(Text)`
  text-align: center;
`;

export const Header = styled.div`
  margin-bottom: 22px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const MonthButtonHolder = styled.div`
  display: grid;
  grid-gap: 8px;
  grid-template-columns: 24px 24px;
`;

export const MonthButton = styled.button`
  height: 24px;
  width: 24px;
  background: ${({ theme }) => theme.colors.grayscale};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity ease-out 0.2s;
  &:hover {
    opacity: 0.7;
  }
`;

export const ApplyButton = styled(Button)`
  width: 100%;
  margin-top: 16px;
`;

const TimeInputs = styled.div`
  display: flex;
  align-items: center;
  margin-top: 24px;
`;

const Dots = styled(Text)`
  flex: 1;
  text-align: center;
`;

const StyledInput = styled(Input)`
  width: 98px;
  border-color: ${({ theme }) => theme.colors.lightGray};
  text-align: center;
`;

const DateInput = styled(Input)`
  cursor: pointer;
`;

const Relative = styled.div`
  position: relative;
`;

interface ArrowProps {
  $isActive: boolean;
}

const Arrow = styled.svg<ArrowProps>`
  position: absolute;
  right: 10px;
  top: calc(50% - 10px);
  transition: transform ease-out 0.2s;
  ${({ $isActive }) =>
    $isActive &&
    css`
      transform: rotate(180deg);
    `}
`;

const DropdownsHolder = styled.div`
  display: flex;
  gap: 8px;
`;

const Dropdown = styled.div`
  position: relative;
`;

const DropdownButton = styled.button`
  ${({ theme }) => css`
    font-family: ${theme.typography.button.fontFamily};
  `}
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  display: flex;
  align-items: center;
  height: 30px;
  cursor: pointer;
  font-weight: 700;
  font-size: 14px;
  width: 96px;
  justify-content: center;
  transition: border-color ease-out 0.2s;
  &:hover {
    border-color: rgba(34, 34, 34, 0.25);
  }
  &:active {
    border-color: ${({ theme }) => theme.colors.gray};
  }
`;

const YearDropdownButton = styled(DropdownButton)`
  width: 60px;
`;

const DropdownSelections = styled.div`
  position: absolute;
  background-color: ${({ theme }) => theme.colors.white};
  top: calc(100% + 8px);
  left: 0;
  border-radius: 4px;
  padding: 2px 2px 2px 4px;
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  width: 124px;
  z-index: 1;
  max-height: 216px;
  overflow-y: auto;
  ${scrollBarStyles};
`;

const DropdownSelection = styled.button<{ $selected?: boolean }>`
  ${({ theme }) => css`
    font-family: ${theme.typography.button.fontFamily};
  `}
  width: 100%;
  font-size: 14px;
  line-height: 22px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray};
  transition: all ease-out 0.2s;
  margin: 4px 0;
  border-radius: 4px;
  &:hover {
    background-color: ${({ theme }) => theme.colors.grayscaleLight};
    color: ${({ theme }) => theme.colors.black};
  }
  ${({ theme, $selected }) =>
    $selected &&
    css`
      color: ${theme.colors.black};
      background-color: ${theme.colors.grayscaleLight};
    `}
`;

interface Props<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  type?: 'date' | 'datetime' | 'time';
  id?: string;
  disabled?: boolean;
  disableDayDimming?: boolean;
}

const Datepicker = <T extends FieldValues>({
  name,
  control,
  type = 'datetime',
  id,
  disabled,
  disableDayDimming,
}: Props<T>) => {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const now = DateTime.now();

  const [selectedDay, setSelectedDay] = useState(
    value ? DateTime.fromISO(value) : now.set({ hour: 0, minute: 0 }),
  );

  const [currentMonth, setCurrentMonth] = useState(selectedDay);

  const [showDatePicker, setShowDatePicker] = useState(false);

  const [showMonthDropdown, setShowMonthDropdown] = useState(false);

  const [showYearDropdown, setShowYearDropdown] = useState(false);

  const firstMothDay = currentMonth.startOf('month');

  const lastMonthDay = currentMonth.endOf('month');

  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setShowDatePicker(false));

  const calendar = buildCalendar(currentMonth);

  const dateTimeValue = value ? DateTime.fromISO(value) : null;

  const getPreviousMonth = () => {
    return currentMonth.minus({ months: 1 });
  };

  const getNextMonth = () => {
    return currentMonth.plus({ months: 1 });
  };

  const getCurrentMonthName = () => {
    return currentMonth.toFormat('MMMM');
  };

  const getCurrentYear = () => {
    return currentMonth.toFormat('y');
  };

  const onHoursChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);

    if (value > 23) return;

    if (!value || isNaN(value)) {
      setSelectedDay((prev) => prev.set({ hour: 0 }));
    } else {
      setSelectedDay((prev) => prev.set({ hour: value }));
    }
  };

  const onMinutesChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);

    if (value > 59) return;

    if (!value || isNaN(value)) {
      setSelectedDay((prev) => prev.set({ minute: 0 }));
    } else {
      setSelectedDay((prev) => prev.set({ minute: value }));
    }
  };

  const onHoursKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const value = parseInt(target.value);

    if (e.key === 'ArrowUp') {
      if (value >= 0 && value < 24) {
        setSelectedDay((prev) => prev.set({ hour: value + 1 }));
      }

      return;
    }

    if (e.key === 'ArrowDown') {
      if (value >= 0 && value < 24) {
        setSelectedDay((prev) => prev.set({ hour: value - 1 }));
      }
    }
  };

  const onMinutesKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const value = parseInt(target.value);

    if (e.key === 'ArrowUp') {
      if (value >= 0 && value < 60) {
        setSelectedDay((prev) => prev.set({ minute: value + 1 }));
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      if (value >= 0 && value < 60) {
        setSelectedDay((prev) => prev.set({ minute: value - 1 }));
      }
    }
  };

  const isTimeSelectable = type !== 'date';
  const isDateSelectable = type !== 'time';

  const setDatepickerValues = () => {
    if (type === 'time') {
      onChange(selectedDay.toFormat('HH:mm'));
      setShowDatePicker((prev) => !prev);
      return;
    }

    const dateString = isTimeSelectable
      ? selectedDay.toISO()
      : selectedDay.toISODate();

    onChange(dateString);
    setShowDatePicker((prev) => !prev);
  };

  const inputValue =
    type === 'time'
      ? dateTimeValue?.toFormat('HH:mm')
      : isTimeSelectable
        ? dateTimeValue?.toFormat('yyyy/LL/dd   HH:mm') ?? ''
        : dateTimeValue?.toFormat('yyyy/LL/dd') ?? '';

  const reset = () => {
    onChange('');
    setShowDatePicker(false);
    setSelectedDay(DateTime.now().startOf('day'));
  };

  return (
    <>
      <Relative ref={ref}>
        <Arrow
          $isActive={showDatePicker}
          height="20"
          width="20"
          viewBox="0 0 20 20"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
        </Arrow>
        <DateInput
          $hasError={!!error}
          name={name}
          value={inputValue}
          placeholder="Select"
          onClick={() => setShowDatePicker((prev) => !prev)}
          readOnly
          id={id}
          disabled={disabled}
        />
        {showDatePicker && (
          <Box>
            {isDateSelectable && (
              <>
                <BoxTop $withoutTime={!isTimeSelectable}>
                  <Header>
                    <DropdownsHolder>
                      <Dropdown>
                        <DropdownButton
                          type="button"
                          onClick={(e: MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            setShowMonthDropdown((prev) => !prev);
                          }}
                        >
                          {getCurrentMonthName()}
                        </DropdownButton>
                        {!!showMonthDropdown && (
                          <DropdownSelections>
                            <>
                              {months.map((month, index) => (
                                <DropdownSelection
                                  onClick={(
                                    e: MouseEvent<HTMLButtonElement>,
                                  ) => {
                                    setShowMonthDropdown(false);
                                    e.stopPropagation();
                                    setCurrentMonth(
                                      currentMonth.set({ month: index + 1 }),
                                    );
                                  }}
                                  $selected={getCurrentMonthName() === month}
                                  type="button"
                                  key={index}
                                >
                                  {month}
                                </DropdownSelection>
                              ))}
                            </>
                          </DropdownSelections>
                        )}
                      </Dropdown>
                      <Dropdown>
                        <YearDropdownButton
                          type="button"
                          onClick={(e: MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            setShowYearDropdown((prev) => !prev);
                          }}
                        >
                          {getCurrentYear()}
                        </YearDropdownButton>
                        {!!showYearDropdown && (
                          <DropdownSelections>
                            <>
                              {Array.from(Array(100).keys())
                                .map(
                                  (key) =>
                                    4 + parseInt(now.toFormat('y')) - key,
                                )
                                .map((year, index) => (
                                  <DropdownSelection
                                    onClick={(
                                      e: MouseEvent<HTMLButtonElement>,
                                    ) => {
                                      e.stopPropagation();
                                      setShowYearDropdown(false);
                                      setCurrentMonth(
                                        currentMonth.set({ year: year }),
                                      );
                                    }}
                                    // $selected={true}
                                    type="button"
                                    key={index}
                                  >
                                    {year}
                                  </DropdownSelection>
                                ))}
                            </>
                          </DropdownSelections>
                        )}
                      </Dropdown>
                    </DropdownsHolder>
                    <MonthButtonHolder>
                      <MonthButton
                        type="button"
                        onClick={() => setCurrentMonth(getPreviousMonth())}
                      >
                        <svg
                          width="7"
                          height="12"
                          viewBox="0 0 7 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M5.75729 1.75748L1.51465 6.00012L5.75729 10.2428"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </MonthButton>
                      <MonthButton
                        type="button"
                        onClick={() => setCurrentMonth(getNextMonth())}
                      >
                        <svg
                          width="7"
                          height="12"
                          viewBox="0 0 7 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1.75736 1.75748L6 6.00012L1.75736 10.2428"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </MonthButton>
                    </MonthButtonHolder>
                  </Header>
                  <Days>
                    <WeekDay $type="caption">Mo</WeekDay>
                    <WeekDay $type="caption">Tu</WeekDay>
                    <WeekDay $type="caption">We</WeekDay>
                    <WeekDay $type="caption">Th</WeekDay>
                    <WeekDay $type="caption">Fr</WeekDay>
                    <WeekDay $type="caption">Sa</WeekDay>
                    <WeekDay $type="caption">Su</WeekDay>
                  </Days>
                  <Grid>
                    {calendar.days.map((day: DateTime, index: number) => {
                      return (
                        <Day
                          type="button"
                          as="button"
                          key={index}
                          $type="caption"
                          $isOtherMonthDay={
                            !disableDayDimming &&
                            (day < firstMothDay ||
                              day > lastMonthDay ||
                              day.plus({ days: 1 }) < now)
                          }
                          onClick={() =>
                            setSelectedDay((prev) =>
                              day.set({
                                hour: parseInt(prev.toFormat('HH')),
                                minute: parseInt(prev.toFormat('mm')),
                              }),
                            )
                          }
                          $isSelected={
                            selectedDay.toISODate() === day.toISODate()
                          }
                        >
                          {day.toFormat('d')}
                        </Day>
                      );
                    })}
                  </Grid>
                </BoxTop>
                <Break />
              </>
            )}

            <BoxBottom>
              {isTimeSelectable && (
                <TimeInputs>
                  <StyledInput
                    value={pad(selectedDay.toFormat('H'))}
                    onChange={onHoursChange}
                    onKeyDown={onHoursKeyDown}
                  />
                  <Dots $type="caption">:</Dots>
                  <StyledInput
                    value={pad(selectedDay.toFormat('m'))}
                    onChange={onMinutesChange}
                    onKeyDown={onMinutesKeyDown}
                  />
                </TimeInputs>
              )}
              <ApplyButton
                onClick={() => {
                  setDatepickerValues();
                }}
              >
                Apply
              </ApplyButton>
              <ApplyButton type="button" onClick={reset} color="secondary">
                Clear
              </ApplyButton>
            </BoxBottom>
          </Box>
        )}
      </Relative>
    </>
  );
};

export default Datepicker;
